import EmptyState from '@app/src/components/EmptyState'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useGroupBySelector } from '@app/src/context/GroupBySelectorContext'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import { br } from '@app/src/utils/translationMarkup'
import { AmplitudeTrackingEvents } from '@app/src/wf-constants'
import { BubbleChartOutlined } from '@mui/icons-material'
import { Grid, Skeleton, Stack } from '@mui/material'
import type { ReactElement } from 'react'
import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { dataHubAllowedFilters } from '.'
import type { StatisticsForQuestionPerGroup } from '../../DataHubScene'
import type { InsightsSubTabs } from './DataHubInsightsScene'
import DataHubInsightsCard from './Insights/DataHubInsightsCard'

interface DataHubInsightsProps {
  questions: StatisticsForQuestionPerGroup[] | undefined
  isLoading: boolean
  activeTab: InsightsSubTabs
  periodNames: string[]
}

const questionFilters = [
  'requestItem.questionId',
  'requestItem.template.options.id',
  'response.request.questionnaireTemplateId',
].map(q => q.toLowerCase())

export const GraphSkeleton = () => (
  <Stack p={2} spacing={1}>
    <Stack direction="row" spacing={2}>
      <Skeleton variant="rounded" height={30} width="100%" />
      <Skeleton variant="rounded" height={30} width={200} />
    </Stack>
    <Skeleton variant="rounded" height={15} width="50%" />
    <Skeleton variant="rounded" height={350} width="100%" />
    <Skeleton variant="rounded" height={15} width="30%" />
  </Stack>
)

const getCardWidth = (question: StatisticsForQuestionPerGroup): 6 | 12 => {
  const itemsCount = Math.max(
    question.resultForGroups?.[0]?.optionStatisticsViews?.[0]?.items?.length ?? 0,
    question.resultForGroups?.length ?? 0,
  )

  return itemsCount > 8 ? 12 : 6
}

const renderCard = (question: StatisticsForQuestionPerGroup, index: number, width: 6 | 12, periodNames: string[]) => {
  const gridProps = width === 12 ? { xs: 12 } : { xs: 12, md: 6 }
  const isFullWidth = width === 12

  return (
    <Grid key={question.id} item {...gridProps}>
      <DataHubInsightsCard statistics={question} index={index} periodNames={periodNames} isFullWidth={isFullWidth} />
    </Grid>
  )
}

const renderCardsFromQuestions = (questions: StatisticsForQuestionPerGroup[] | undefined, periodNames: string[]) => {
  if (!questions) return []
  const cards: ReactElement[] = []
  let i = 0

  while (i < questions.length) {
    const current = questions[i]
    const next = questions[i + 1]

    const currentWidth = getCardWidth(current)
    const nextWidth = next ? getCardWidth(next) : null

    const forceFull = currentWidth === 6 && nextWidth === 12
    const isFull = currentWidth === 12 || forceFull

    cards.push(renderCard(current, i, isFull ? 12 : 6, periodNames))

    const shouldPairWithNextQuestion = !isFull && nextWidth === 6
    if (shouldPairWithNextQuestion) {
      i++
      const paired = questions[i]
      cards.push(renderCard(paired, i, 6, periodNames))
    }

    i++
  }

  return cards
}

const DataHubInsights: React.FC<DataHubInsightsProps> = ({ questions, isLoading, activeTab, periodNames }) => {
  const { formatMessage } = useIntl()
  const userFilters = useGetApiQueryFilters(dataHubAllowedFilters)
  const { groupBy } = useGroupBySelector()
  const { trackEvent } = useAmplitude()

  const anyQuestionFilterSet = useMemo(
    () => userFilters.some(filter => questionFilters.includes(filter.name.toLowerCase())),
    [userFilters],
  )

  useEffect(() => {
    if (anyQuestionFilterSet && !isLoading && questions) {
      trackEvent({
        name: AmplitudeTrackingEvents.Analyze.DataHub.Insights.ViewedInsights,
        eventProps: {
          subPageViewed: activeTab,
          groupBy,
          userFilters: userFilters.map(f => f.name),
        },
      })
    }
  }, [activeTab, anyQuestionFilterSet, isLoading, questions, trackEvent, userFilters, groupBy])

  const loadingSkeletons = useMemo(
    () =>
      Array.from({ length: 4 }).map((_, index) => (
        <Grid item md={6} key={index}>
          <GraphSkeleton />
        </Grid>
      )),
    [],
  )

  const renderedCards = useMemo(() => {
    return renderCardsFromQuestions(questions, periodNames)
  }, [questions, activeTab, groupBy, periodNames])

  if (!anyQuestionFilterSet)
    return (
      <EmptyState
        title={formatMessage({ id: 'statistics.dataInsights.emptyStateTitle' }, { br })}
        description={formatMessage({ id: 'statistics.dataInsights.emptyStateDescription' })}
        iconComponent={BubbleChartOutlined}
      />
    )

  if (isLoading || !questions)
    return (
      <Grid container spacing={2} sx={{ paddingRight: 4 }}>
        {loadingSkeletons}
      </Grid>
    )

  if (!isLoading && !questions.length) {
    return (
      <EmptyState
        iconComponent={BubbleChartOutlined}
        title={formatMessage({ id: 'schemas.dataHubOverTime.emptyFilterState.title' })}
        description={formatMessage({ id: 'statistics.noResults' })}
      />
    )
  }

  return (
    <Grid container spacing={2} sx={{ pr: 4, pb: 2 }}>
      {renderedCards}
    </Grid>
  )
}

export default DataHubInsights
